import type {
  FixtureMeta,
  JudgeCalibrationFile,
  ReportJson,
} from '../../../../functions/tests/eval/recommendations/types'

/**
 * Client wrapper for the dev-only eval-fixture file bridge (see
 * `vite-plugins/evalFixtureBridge.ts`). Every call goes to a `/api/eval/*`
 * endpoint that only exists under the Vite dev server.
 *
 * In a built/hosted context those endpoints don't exist and the SPA fallback
 * serves index.html — so a "successful" fetch returns HTML, not JSON. We detect
 * that (non-JSON content-type or a parse failure) and surface it as
 * {@link BridgeUnavailableError} so callers can show the inline "fixture file
 * access requires the dev server" notice instead of crashing.
 */

export class BridgeUnavailableError extends Error {
  constructor(message = 'fixture file access requires the dev server') {
    super(message)
    this.name = 'BridgeUnavailableError'
  }
}

export interface FixtureListItem {
  slug: string
  meta: FixtureMeta
}

export interface ResultFileListItem {
  file: string
  mtimeMs: number
}

const BASE = '/api/eval'

/**
 * Parse a bridge response as JSON, translating the built/hosted-mode "HTML
 * fallback" and network failures into {@link BridgeUnavailableError}, and
 * non-2xx JSON errors into a plain Error carrying the server message.
 */
async function parseJson<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') ?? ''
  // The SPA fallback (built mode) answers 200 text/html — treat as unavailable.
  if (!contentType.includes('application/json')) {
    throw new BridgeUnavailableError()
  }
  let body: unknown
  try {
    body = await res.json()
  } catch {
    throw new BridgeUnavailableError()
  }
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Bridge request failed (${res.status})`
    throw new Error(message)
  }
  return body as T
}

async function bridgeFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, init)
  } catch {
    // Dev server down or endpoint unreachable.
    throw new BridgeUnavailableError()
  }
  return parseJson<T>(res)
}

/** True when the bridge answered the fixtures list (dev server present). */
export async function isBridgeAvailable(): Promise<boolean> {
  try {
    await listFixtures()
    return true
  } catch (err) {
    if (err instanceof BridgeUnavailableError) return false
    // A real error (e.g. 500) still means the bridge is present.
    return true
  }
}

export async function listFixtures(): Promise<FixtureListItem[]> {
  return bridgeFetch<FixtureListItem[]>('/fixtures')
}

export async function getFixture(slug: string): Promise<FixtureMeta> {
  return bridgeFetch<FixtureMeta>(`/fixtures/${encodeURIComponent(slug)}`)
}

export async function saveFixture(
  slug: string,
  meta: FixtureMeta
): Promise<{ ok: boolean; slug: string }> {
  return bridgeFetch<{ ok: boolean; slug: string }>(
    `/fixtures/${encodeURIComponent(slug)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    }
  )
}

export async function listResults(): Promise<ResultFileListItem[]> {
  return bridgeFetch<ResultFileListItem[]>('/results')
}

export async function getResult(file: string): Promise<ReportJson> {
  return bridgeFetch<ReportJson>(`/results/${encodeURIComponent(file)}`)
}

/**
 * Read the calibration file sitting next to `reportFile` (the judge-calibration
 * verdicts for that report), or `null` when none has been written yet. A missing
 * file answers 404 — the only non-2xx we treat as "not an error"; everything
 * else (bridge down, 500, malformed) still throws.
 */
export async function getCalibration(
  reportFile: string
): Promise<JudgeCalibrationFile | null> {
  let res: Response
  try {
    res = await fetch(
      `${BASE}/results/${encodeURIComponent(reportFile)}/calibration`
    )
  } catch {
    throw new BridgeUnavailableError()
  }
  const contentType = res.headers.get('content-type') ?? ''
  // Built-mode SPA fallback answers 200 text/html — treat as unavailable.
  if (!contentType.includes('application/json')) {
    throw new BridgeUnavailableError()
  }
  if (res.status === 404) return null
  return parseJson<JudgeCalibrationFile>(res)
}

export async function saveCalibration(
  reportFile: string,
  calibration: JudgeCalibrationFile
): Promise<{ ok: boolean; file: string }> {
  return bridgeFetch<{ ok: boolean; file: string }>(
    `/results/${encodeURIComponent(reportFile)}/calibration`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(calibration),
    }
  )
}
